import { default as utils } from './utils.js';
import Query from './query.js';
import Tastet from '../tastet.js';

let Mixins = {};

Mixins.Base = {
  // props -------------------------------------------------------------------
  utils: Utils,
  // methods -----------------------------------------------------------------
  $: function (selector) {
    return new Query(selector, { context: this });
  },
  apply: function (callback, params) {
    return Utils.apply(callback, params, this);
  },
  each: function (arr, args, callback) {
    return utils.each.context(this, arr, args, callback);
  },
  wait: function (time, callback, debounceKey) {
    var self = this;
    var timeouts = this._waitTimeouts || (this._waitTimeouts = {});

    if (debounceKey && debounceKey in timeouts) {
      clearTimeout(timeouts[debounceKey]);
    }
    var timeout = setTimeout(function () {
      if (Utils.is.fn(callback)) {
        callback.apply(self);
      }
      timeouts[debounceKey] = null;
    }, time);

    if (debounceKey) {
      timeouts[debounceKey] = timeout;
    }
  },
  require: function (url, callback) {
    if (~url.indexOf('.css')) {
      url = (Tastet.css_url || '') + url;
    } else if (!~url.indexOf('http')) {
      url = (Tastet.js_url || '') + url;
    }
    utils.media.require(url, { context: this }, callback);
  },
  http: function (url, data, args) {
    if (arguments.length <= 2) {
      args = data;
      data = null;
    }

    if (utils.is.fn(args)) {
      args = { callback: args };
    }

    args = args || {};
    args.data = args.data || data || {};
    args.delay = args.delay || 0;
    args.unique = 'unique' in args ? args.unique : false;
    args.context = this;

    // this is a "wp" ajax, using the AJAX query
    if (!~url.indexOf('/')) {
      url = (Tastet.settings.ajax_url || '') + '?action=' + url;
    }

    if (Tastet.settings.ajax_nonce) {
      args.data.nonce = Tastet.settings.ajax_nonce;
    }

    function _call() {
      var ajax = utils.media.http(url, args);

      if (args.unique) {
        var https = this._https || (this._https = {});
        utils.media.cancelHttp(https[url]);
        https[url] = ajax;
      }
    }

    if (args.delay) {
      this.wait(args.delay, _call, args.unique ? url : null);
    } else {
      _call();
    }
  },
  when: function (key, callback, data) {
    this._when = this._when || {};
    return utils.fn.when(key, callback, data, this);
  },
};

/**
 * Simple Mixin for Events. Deals with HTMLElement or without.
 * It always makes sure the "this" in the callback is the instance
 */
var LAZY_EVENTS = (window.__lazy_events__ = {});

Mixins.Events = {
  __MIXIN_EVENTS__: true, // little trick to know if a instance has Mixins.Events
  on: function (el, name, args) {
    if (el === undefined) {
      return;
    }

    // adding multiple events to multiple items
    if (utils.is.arr(el, true)) {
      utils.each(el, { context: this }, function (child) {
        this.on(child, name, args);
      });
      return this;
    }

    // make sure the _events object is local to the module
    this._events = this._events || [];

    // there's no item, so it's this
    if (typeof el === 'string') {
      args = name;
      name = el;
      el = null;
    }

    // a callback can be a function OR an array of functions
    if (utils.is.either(args, 'function,array')) {
      args = { callback: args };
    }

    args = args || {};
    args.context = this;
    args.delegate = args.delegate || '';
    args.data = args.data || {};
    args.callback = args.callback || null;

    var self = this;

    function _ctx(fn) {
      if (utils.is.fn(fn)) {
        return fn.bind(self);
      } else {
        // @info callback has to be a function when connecting 2 modules together
        console.log('event binding needs to be with a function');
      }
    }

    // global-ish events (lazy since the element doenst need to exists)
    if (name[0] === '#') {
      LAZY_EVENTS[name] = LAZY_EVENTS[name] || [];
      LAZY_EVENTS[name].push(args);
    } else if (el && el.__MIXIN_EVENTS__) {
      args.callback = _ctx(args.callback); // make sure the context of THIS is kept
      el.on(name, args);
    } else if (el instanceof Query) {
      el.on(name, args);
    } else if (el) {
      args.callback = _ctx(args.callback); // make sure the context of THIS is kept
      utils.dom.events.add(el, name, args);
    } else {
      // @nicetohave make sure event isn't added multiple time, even if the args is a callback at first
      this._events[name] = this._events[name] || [];

      // make sure there's only 1 time that callback
      if (!~this._events[name].indexOf(args)) {
        this._events[name].push(args);
      }
    }

    return this;
  },
  off: function (el, name, callback) {
    if (!this._events) return;

    // @nicetohave events can't be removed right now, only in the case of DomElement being removed do we need this functionality.
    //      Since we use delegate events in these kind of cases, this function isn't used

    return this;
  },
  trigger: function (el, names, e) {
    if (!this._events || !this.hasEvents()) return;

    if (typeof el === 'string') {
      e = names;
      names = el;
      el = null;
    }

    e = e || {};
    e.target = this;
    e.timestamp = +new Date();

    utils.each(names, { separator: ' ', context: this }, function (name) {
      if (el) {
        utils.dom.events.trigger(el, names, [e]);
      } else {
        var events = this._events[name] || [];
        var i, l, event;

        // check for lazy events
        var lazy = '#' + this.id + ':' + name;
        if (lazy in LAZY_EVENTS) {
          events = events.concat(LAZY_EVENTS[lazy] || []);
        }

        for (i = 0, l = events.length; i < l; ++i) {
          event = events[i];

          if (utils.is.fn(event.callback)) {
            event.callback.apply(event.context || this, [e, event]);
          }
        }
      }
    });

    return this;
  },
  hasEvents: function (value) {
    if (this.__hasEvents__ === undefined) {
      this.__hasEvents__ = true;
    }

    if (utils.is.bool(value)) {
      this.__hasEvents__ = value;
      return this;
    }

    return this.__hasEvents__;
  },
};

Mixins.Filters = {
  __MIXIN_FILTERS__: true,
  addFilter: function (name, callback) {},
  removeFilter: function (name, callback) {},
  applyFilters: function (name, value) {},
};

Mixins.States = {
  __MIXIN_STATES__: true,
  state: function (key, value) {
    var set = utils.toSet(key, value);
    var states = (this._states = this._states || {});

    if (set) {
      var changes = null;
      for (var i in set) {
        var o = states[i];
        var v = set[i];

        if (o !== v) {
          changes = changes || {};
          changes[i] = { value: v, old: o };
          this.apply(this.onStateChange, [i, v, o]);
        }

        states[i] = v;
      }

      if (changes) {
        this.apply(this.onStatesChange, changes);
      }
    } else if (key) {
      return key in states ? states[key] : null;
    } else {
      return states;
    }
  },
  toggleState: function (name) {
    var s = !!this.state(name);
    this.state(name, !s);
  },
};

/**
 * Mixin to add a Prefix of the class that logs stuff
 */
Mixins.Log = {
  logPrefix: function () {
    return '[Tastet:' + this.constructor.name + ']';
  },
  _log: function (type, args) {
    var params = Array.prototype.slice.call(args);
    var prefix = this.logPrefix();
    console[type].apply(null, [prefix].concat(params));
    return this;
  },
  log: function () {
    this._log('log', arguments);
  },
  warn: function () {
    this._log('warn', arguments);
  },
  error: function () {
    this._log('error', arguments);
  },
};

export default Mixins;
